/**
 * MobileFaceIdPage.tsx - Мобильная страница прохождения FaceID для авторизации на ПК по QR-коду
 * Корпоративный портал Zerde Pharmacy
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  CheckCircle,
  Visibility,
  ArrowBack,
  ArrowForward,
  SentimentSatisfiedAlt,
  ScreenRotation,
  ArrowDownward,
  ZoomIn,
  WarningAmber
} from '@mui/icons-material';
import { ScanFace, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { API_CONFIG } from './config/authConfig';

export type LivenessAction = 'turn_left' | 'turn_right' | 'smile' | 'tilt_head' | 'nod' | 'move_closer';
export type MobileAuthStep =
  | 'init'
  | 'loading_models'
  | 'starting_camera'
  | 'centering'
  | 'challenge'
  | 'matching'
  | 'success'
  | 'error';

const MobileFaceIdPage: React.FC = () => {
  const [token, setToken] = useState<string>('');
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [targetUsername, setTargetUsername] = useState<string>('');
  const [step, setStep] = useState<MobileAuthStep>('init');
  const [statusMessage, setStatusMessage] = useState<string>('Готов к сканированию лица');
  const [subMessage, setSubMessage] = useState<string>('');
  const [progress, setProgress] = useState<number>(10);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userNameSuccess, setUserNameSuccess] = useState<string>('');

  const [actionsSequence, setActionsSequence] = useState<LivenessAction[]>([]);
  const [currentActionIndex, setCurrentActionIndex] = useState<number>(0);
  const [completedActions, setCompletedActions] = useState<LivenessAction[]>([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const isLoopActiveRef = useRef<boolean>(false);

  const livenessSessionIdRef = useRef<string | null>(null);
  const actionsSequenceRef = useRef<LivenessAction[]>([]);
  const completedActionsListRef = useRef<LivenessAction[]>([]);
  const isProcessingFrameRef = useRef<boolean>(false);
  const hiddenCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    const modeParam = params.get('mode');
    const userParam = params.get('username');

    if (modeParam === 'register') {
      setMode('register');
    }
    if (userParam) {
      setTargetUsername(userParam);
    }

    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setStep('error');
      setErrorMessage('Отсутствует токен авторизации. Отсканируйте QR-код на компьютере заново.');
    }
  }, []);

  const stopAll = () => {
    isLoopActiveRef.current = false;
    isProcessingFrameRef.current = false;
    if (animFrameRef.current) {
      window.clearTimeout(animFrameRef.current);
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopAll();
    };
  }, []);

  // Захват текущего видеокадра в base64 JPEG
  const captureFrameBase64 = (): string | null => {
    if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) return null;
    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) return null;

    if (!hiddenCanvasRef.current) {
      hiddenCanvasRef.current = document.createElement('canvas');
    }
    const canvas = hiddenCanvasRef.current;
    canvas.width = 480;
    canvas.height = 360;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, 480, 360);
    return canvas.toDataURL('image/jpeg', 0.85);
  };

  const updateActionPrompt = (action: LivenessAction) => {
    if (action === 'turn_left') {
      setStatusMessage('Поверните голову слегка влево');
      setSubMessage('Плавный поворот в сторону левого плеча');
    } else if (action === 'turn_right') {
      setStatusMessage('Поверните голову слегка вправо');
      setSubMessage('Плавный поворот в сторону правого плеча');
    } else if (action === 'smile') {
      setStatusMessage('Улыбнитесь');
      setSubMessage('Слегка приподнимите уголки губ');
    } else if (action === 'tilt_head') {
      setStatusMessage('Наклоните голову к плечу');
      setSubMessage('Слегка наклоните голову влево или вправо');
    } else if (action === 'nod') {
      setStatusMessage('Кивните головой вниз');
      setSubMessage('Слегка опустите подбородок');
    } else if (action === 'move_closer') {
      setStatusMessage('Приблизьтесь к камере');
      setSubMessage('Подайтесь немного вперед к объективу');
    }
  };

  const resetChallengeProgress = (reason: string) => {
    completedActionsListRef.current = [];
    setCompletedActions([]);
    setCurrentActionIndex(0);
    setStep('centering');
    setStatusMessage('Поместите лицо в центр овала');
    setSubMessage(reason);
    setProgress(40);
  };

  // Цикл отправки кадров на сервер для liveness
  const runServerLivenessLoop = async () => {
    if (!isLoopActiveRef.current || !videoRef.current) return;

    if (!isProcessingFrameRef.current && livenessSessionIdRef.current) {
      const frame = captureFrameBase64();
      if (frame) {
        isProcessingFrameRef.current = true;
        try {
          const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/liveness/process-frame`, {
            livenessSessionId: livenessSessionIdRef.current,
            frame
          });

          if (res.data?.success) {
            const data = res.data;

            if (data.phase === 'completed') {
              isLoopActiveRef.current = false;
              setStep('matching');
              setStatusMessage('Проверка живости пройдена!');
              setSubMessage(mode === 'register' ? 'Сохранение биометрии (ArcFace)...' : 'Авторизация в системе (ArcFace)...');
              setProgress(92);

              await handleCompleteVerification();
              return;
            }

            if (data.phase === 'calibrating') {
              setStep('centering');
              if (data.faceDetected) {
                setStatusMessage('Поместите лицо в центр овала');
                const samples = data.calibrationProgress || 0;
                setSubMessage(`Калибровка черт лица (${samples}/5)...`);
                setProgress(20 + samples * 4);
              } else {
                setStatusMessage('Лицо не обнаружено');
                setSubMessage('Посмотрите прямо в камеру');
              }
            }

            if (data.phase === 'challenge') {
              setStep('challenge');
              if (data.currentAction) {
                updateActionPrompt(data.currentAction);
                const currentIdx = actionsSequenceRef.current.indexOf(data.currentAction);
                if (currentIdx >= 0) {
                  setCurrentActionIndex(currentIdx);
                }
              }

              if (Array.isArray(data.completedActions)) {
                completedActionsListRef.current = data.completedActions;
                setCompletedActions(data.completedActions);
                const total = Math.max(actionsSequenceRef.current.length, 1);
                setProgress(45 + (data.completedActions.length / total) * 45);
              }

              if (data.actionCompleted) {
                setSubMessage('Действие выполнено!');
              }
            }
          } else if (res.data?.code === 'ANTI_SWAP_TRIGGERED') {
            resetChallengeProgress('Обнаружено резкое смещение лица. Держите телефон ровно.');
          }
        } catch (err: any) {
          if (err.response?.data?.code === 'SESSION_EXPIRED') {
            stopAll();
            setErrorMessage('Время сессии проверки истекло. Пожалуйста, повторите.');
            setStep('error');
            return;
          }
          console.warn('[MobileFaceId] Ошибка кадра на сервере:', err.message);
        } finally {
          isProcessingFrameRef.current = false;
        }
      }
    }

    if (isLoopActiveRef.current) {
      animFrameRef.current = window.setTimeout(runServerLivenessLoop, 120) as any;
    }
  };

  const handleCompleteVerification = async () => {
    stopAll();

    try {
      if (mode === 'register') {
        const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/qr-register-and-complete`, {
          qrToken: token,
          livenessSessionId: livenessSessionIdRef.current,
          username: targetUsername
        });

        if (res.data?.success) {
          setProgress(100);
          setStep('success');
          setUserNameSuccess(res.data.fullName || targetUsername || 'Сотрудник');
          setStatusMessage('Биометрия лица сохранена (ArcFace)!');
          setSubMessage('Профиль на компьютере обновлен');
        } else {
          setErrorMessage(res.data?.message || 'Ошибка сохранения биометрии');
          setStep('error');
        }
      } else {
        const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/qr-verify-and-complete`, {
          qrToken: token,
          livenessSessionId: livenessSessionIdRef.current
        });

        if (res.data?.success) {
          setProgress(100);
          setStep('success');
          setUserNameSuccess(res.data.fullName || 'Сотрудник');
          setStatusMessage('Вход на компьютере выполнен!');
          setSubMessage('Вы успешно авторизованы');
        } else {
          setErrorMessage(res.data?.message || 'Лицо не найдено среди зарегистрированных сотрудников.');
          setStep('error');
        }
      }
    } catch (authErr: any) {
      console.error('[MobileFaceId] Ошибка завершения:', authErr);
      const msg =
        authErr.response?.data?.message ||
        'Проверка не пройдена. Попробуйте еще раз.';
      setErrorMessage(msg);
      setStep('error');
    }
  };

  const startAuthFlow = async () => {
    if (!token) return;
    stopAll();
    setErrorMessage(null);
    setStep('loading_models');
    setStatusMessage('Инициализация сессии FaceID...');
    setSubMessage('Подключение к серверной нейросети InsightFace');
    setProgress(15);

    // Запрос серверной liveness-сессии
    try {
      const chalRes = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/liveness/start`);
      if (chalRes.data?.success && chalRes.data?.livenessSessionId) {
        livenessSessionIdRef.current = chalRes.data.livenessSessionId;
        actionsSequenceRef.current = chalRes.data.sequence || [];
        completedActionsListRef.current = [];

        setActionsSequence(chalRes.data.sequence || []);
        setCurrentActionIndex(0);
        setCompletedActions([]);
      } else {
        throw new Error('Сервер не вернул сессию проверки');
      }
    } catch (chalErr) {
      console.error('[MobileFaceId] Ошибка старта серверной сессии:', chalErr);
      setErrorMessage('Не удалось получить сессию проверки у сервера. Попробуйте обновить страницу.');
      setStep('error');
      return;
    }

    setStep('starting_camera');
    setStatusMessage('Подключение фронтальной камеры...');
    setSubMessage('Разрешите доступ к камере в браузере');
    setProgress(30);

    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        throw new Error(
          'Доступ к веб-камере не поддерживается в этом браузере или страница открыта без HTTPS. Откройте ссылку в Safari или Chrome через https://'
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: false
      });
      mediaStreamRef.current = stream;

      setStep('centering');
      setStatusMessage('Поместите лицо в центр овала');
      setSubMessage('Смотрите прямо в камеру');
      setProgress(40);

      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;

        let loopStarted = false;
        const onStreamReady = () => {
          if (loopStarted) return;
          loopStarted = true;

          isLoopActiveRef.current = true;
          runServerLivenessLoop();
        };

        video.onloadedmetadata = () => {
          video.play().then(onStreamReady).catch(onStreamReady);
        };
        video.play().then(onStreamReady).catch(() => { });
      }
    } catch (camErr: any) {
      console.error('[MobileFaceId] Ошибка камеры:', camErr);
      setErrorMessage(
        'Не удалось получить доступ к фронтальной камере. Разрешите доступ к камере в настройках браузера и обновите страницу.'
      );
      setStep('error');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0f172a',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        color: '#f8fafc'
      }}
    >
      <Container maxWidth="xs" sx={{ p: 0 }}>
        <Paper
          elevation={4}
          sx={{
            p: 3,
            borderRadius: '24px',
            bgcolor: '#1e293b',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          {/* Верхний логотип и индикатор */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ScanFace className="w-7 h-7 text-emerald-400" />
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
              FaceID <span style={{ color: '#10b981' }}>Zerde</span>
            </Typography>
          </Box>

          <Chip
            icon={<ShieldCheck className="w-4 h-4 text-emerald-400" />}
            label={mode === 'register' ? 'Привязка FaceID к профилю' : 'Авторизация на ПК через телефон'}
            size="small"
            sx={{
              bgcolor: 'rgba(16, 185, 129, 0.12)',
              color: '#34d399',
              fontWeight: 600,
              fontSize: '0.75rem',
              mb: 2.5
            }}
          />

          {/* НАЧАЛЬНЫЙ ЭКРАН */}
          {step === 'init' && (
            <Box sx={{ width: '100%', py: 2 }}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                  border: '2px dashed #10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <ScanFace className="w-12 h-12 text-emerald-400" />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {mode === 'register' ? 'Привязка биометрии лица' : 'Подтверждение личности'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
                {mode === 'register'
                  ? `Для регистрации лица в профиле ${targetUsername ? `(${targetUsername})` : 'сотрудника'} посмотрите в камеру и выполните 2 коротких жеста.`
                  : 'Для авторизации на рабочем компьютере посмотрите во фронтальную камеру и выполните 2 коротких жеста.'}
              </Typography>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={startAuthFlow}
                sx={{
                  py: 1.5,
                  borderRadius: '14px',
                  bgcolor: '#10b981',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
                  '&:hover': { bgcolor: '#059669' }
                }}
              >
                Включить камеру и начать
              </Button>
            </Box>
          )}

          {/* ВИДЕО-СКАНЕР: Всегда смонтирован в DOM во время процесса сканирования */}
          {(step === 'loading_models' || step === 'starting_camera' || step === 'centering' || step === 'challenge' || step === 'matching') && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* Прогресс-бар */}
              <Box sx={{ width: '100%', mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#10b981',
                      borderRadius: 3
                    }
                  }}
                />
              </Box>

              {/* Видео-визир */}
              <Box
                sx={{
                  position: 'relative',
                  width: 260,
                  height: 330,
                  borderRadius: '130px',
                  overflow: 'hidden',
                  border: '3px solid #10b981',
                  boxShadow: '0 0 24px rgba(16, 185, 129, 0.3)',
                  mb: 2.5,
                  bgcolor: '#000'
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: 'scaleX(-1)'
                  }}
                />

                {/* Овальная направляющая рамка */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 12,
                    borderRadius: '120px',
                    border: '2px dashed rgba(255, 255, 255, 0.4)',
                    pointerEvents: 'none'
                  }}
                />

                {/* Оверлей загрузки */}
                {(step === 'loading_models' || step === 'starting_camera') && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(15, 23, 42, 0.88)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      p: 2
                    }}
                  >
                    <CircularProgress size={46} sx={{ color: '#10b981' }} />
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, textAlign: 'center' }}>
                      {statusMessage}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', textAlign: 'center' }}>
                      {subMessage}
                    </Typography>
                  </Box>
                )}

                {/* Оверлей сверки */}
                {step === 'matching' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(15, 23, 42, 0.88)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5
                    }}
                  >
                    <CircularProgress size={42} sx={{ color: '#10b981' }} />
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                      {mode === 'register' ? 'Сохранение биометрии...' : 'Сверка лица...'}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Текстовые инструкции */}
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {statusMessage}
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2, minHeight: 22 }}>
                {subMessage}
              </Typography>

              {/* Список жестов */}
              {step === 'challenge' && actionsSequence.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  {actionsSequence.map((act, idx) => {
                    const isDone = completedActions.includes(act);
                    const isCurr = idx === currentActionIndex;

                    let label = 'Жест';
                    let Icon = ArrowBack;
                    if (act === 'turn_left') { label = 'Влево'; Icon = ArrowBack; }
                    if (act === 'turn_right') { label = 'Вправо'; Icon = ArrowForward; }
                    if (act === 'smile') { label = 'Улыбка'; Icon = SentimentSatisfiedAlt; }
                    if (act === 'tilt_head') { label = 'Наклон'; Icon = ScreenRotation; }
                    if (act === 'nod') { label = 'Кивок'; Icon = ArrowDownward; }
                    if (act === 'move_closer') { label = 'Ближе'; Icon = ZoomIn; }

                    return (
                      <Chip
                        key={idx}
                        icon={<Icon sx={{ fontSize: 16, color: isDone ? '#10b981' : isCurr ? '#38bdf8' : '#64748b' }} />}
                        label={label}
                        size="small"
                        sx={{
                          bgcolor: isDone ? 'rgba(16, 185, 129, 0.2)' : isCurr ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                          color: isDone ? '#34d399' : isCurr ? '#38bdf8' : '#94a3b8',
                          fontWeight: 600,
                          border: isCurr ? '1px solid #38bdf8' : 'none'
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          {/* ЭКРАН УСПЕХА */}
          {step === 'success' && (
            <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: '50%',
                  bgcolor: 'rgba(16, 185, 129, 0.15)',
                  border: '3px solid #10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2.5
                }}
              >
                <CheckCircle sx={{ fontSize: 56, color: '#10b981' }} />
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: '#34d399' }}>
                {mode === 'register' ? 'Биометрия привязана!' : 'Вход выполнен!'}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#f8fafc', mb: 1 }}>
                {userNameSuccess}
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
                {mode === 'register'
                  ? 'Ваше лицо успешно зарегистрировано в системе. Окно в профиле на компьютере обновилось автоматически. Эту страницу на смартфоне можно закрыть.'
                  : 'Компьютер успешно разблокирован. Эту страницу на смартфоне можно закрыть.'}
              </Typography>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => window.close()}
                sx={{
                  py: 1.2,
                  borderRadius: '12px',
                  color: '#94a3b8',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Закрыть вкладку
              </Button>
            </Box>
          )}

          {/* ЭКРАН ОШИБКИ */}
          {step === 'error' && (
            <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <WarningAmber sx={{ fontSize: 60, color: '#f87171', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#f87171' }}>
                {mode === 'register' ? 'Не удалось привязать' : 'Не удалось войти'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 3 }}>
                {errorMessage || 'Произошла ошибка при сканировании.'}
              </Typography>

              <Button
                variant="contained"
                fullWidth
                onClick={startAuthFlow}
                sx={{
                  py: 1.3,
                  borderRadius: '12px',
                  bgcolor: '#3b82f6',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#2563eb' }
                }}
              >
                Повторить попытку
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

// Error Boundary для защиты мобильной страницы от крашей
class MobileErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: any) {
    console.error('[MobileFaceId] Uncaught crash:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#0f172a', color: '#fff', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <WarningAmber sx={{ fontSize: 64, color: '#ef4444', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: '#f87171', mb: 1 }}>
            Ошибка страницы FaceID
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3, wordBreak: 'break-word', maxWidth: 360 }}>
            {this.state.error?.message || 'Непредвиденная ошибка на мобильном устройстве'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ bgcolor: '#10b981', textTransform: 'none', borderRadius: '12px' }}
          >
            Перезагрузить страницу
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const SafeMobileFaceIdPage: React.FC = () => (
  <MobileErrorBoundary>
    <MobileFaceIdPage />
  </MobileErrorBoundary>
);

export default SafeMobileFaceIdPage;
